import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Settings2, CheckSquare, Square } from "lucide-react";
import { TableColumn } from "../Table";

interface ColumnSelectorProps {
  columns: TableColumn<any>[];
  visibleKeys: string[];
  onToggle: (key: string) => void;
  className?: string;
}

const MENU_WIDTH = 256;
const GAP = 8;

function computeMenuLayout(trigger: HTMLButtonElement): {
  top: number;
  left: number;
  listMaxHeight: number;
} {
  const rect = trigger.getBoundingClientRect();
  let left = rect.right - MENU_WIDTH;
  left = Math.max(GAP, Math.min(left, window.innerWidth - MENU_WIDTH - GAP));
  const top = rect.bottom + GAP;
  const headerReserve = 52;
  const listMaxHeight = Math.max(
    120,
    Math.min(400, window.innerHeight - top - GAP - headerReserve),
  );
  return { top, left, listMaxHeight };
}

// const MENU_WIDTH = 256;
// const MENU_OFFSET = 8;

/**
 * Renders the column visibility selector trigger and a portaled dropdown
 * that escapes parent overflow/clipping contexts (e.g. table cards using
 * `overflow-hidden`). The dropdown floats above page content but stays below
 * sticky page headers/sidebars.
 */
export const ColumnSelector: React.FC<ColumnSelectorProps> = ({
  columns,
  visibleKeys,
  onToggle,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [menuLayout, setMenuLayout] = useState<{
    top: number;
    left: number;
    listMaxHeight: number;
  } | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const syncLayout = useCallback(() => {
    const btn = triggerRef.current;
    if (!btn || !isOpen) return;
    setMenuLayout(computeMenuLayout(btn));
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuLayout(null);
      return;
    }
    syncLayout();
  }, [isOpen, syncLayout, columns.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onScrollOrResize = () => syncLayout();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen, syncLayout]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        triggerRef.current?.contains(t) ||
        menuRef.current?.contains(t)
      ) {
        return;
      }
      setIsOpen(false);
    };

    // const handleEscape = (e: KeyboardEvent) => {
    //   if (e.key === "Escape") setIsOpen(false);
    // };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen]);

  const menuPortal =
    mounted &&
    isOpen &&
    menuLayout &&
    createPortal(
      <div
        ref={menuRef}
        className="fixed w-64 rounded-xl border border-slate-200 bg-white py-2 shadow-xl z-[1100] flex flex-col animate-in fade-in zoom-in-95 duration-200 overscroll-contain"
        style={{
          top: menuLayout.top,
          left: menuLayout.left,
          transformOrigin: "top right",
        }}
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-100 px-4 py-2 flex items-center justify-between">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Manage Visibility
          </span>
        </div>
        <div
          className="min-h-0 overflow-y-auto overscroll-contain py-1 scrollbar-thin scrollbar-thumb-slate-200"
          style={{ maxHeight: menuLayout.listMaxHeight }}
          onWheel={(e) => e.stopPropagation()}
        >
          {columns.map((col) => {
            const isRequired = !!col.required;
            const isVisible = isRequired || visibleKeys.includes(col.key);

            return (
              <label
                key={col.key}
                className={`
                    flex items-center gap-3 px-4 py-2.5 text-sm cursor-pointer transition-colors
                    ${isRequired ? "opacity-60 cursor-not-allowed bg-slate-50" : "hover:bg-slate-50 active:bg-slate-100"}
                  `}
              >
                <input
                  type="checkbox"
                  className="hidden"
                  checked={isVisible}
                  onChange={() => !isRequired && onToggle(col.key)}
                  disabled={isRequired}
                />

                {isVisible ? (
                  <CheckSquare
                    size={18}
                    className="text-primary-600 fill-primary-50"
                  />
                ) : (
                  <Square size={18} className="text-slate-300" />
                )}

                <span
                  className={`flex-1 truncate ${isVisible ? "text-slate-900 font-medium" : "text-slate-500 font-normal"}`}
                >
                  {col.label ||
                    (typeof col.title === "string" ? col.title : col.key)}
                </span>

                {isRequired && (
                  <span className="text-[9px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter shrink-0">
                    Required
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>,
      document.body,
    );

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        onClick={() => setIsOpen((o) => !o)}
        className="
          flex items-center gap-2 px-3 py-2 text-sm font-medium 
          text-slate-700 bg-white border border-slate-200 rounded-lg 
          hover:bg-slate-50 transition-all duration-200 shadow-sm
        "
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Settings2 size={16} className="text-slate-500" />
        <span>Columns</span>
      </button>
      {menuPortal}
    </div>
  );
};